import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getMaterialStateColor } from '../../../../utils/colors';
import { formatDate, formatDateToDDMMYYYY, formatDateToYYYYMMDD } from '../../../../utils/dates';
import { exportTypeEnum, getMaterialStateInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import ExportCsvIcon from '../../../ExportCsvIcon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { useAuth } from '../../../../hooks/useAuth';
import { stepModeEnum } from '../../../../constants';

interface RentalsTableProps {
    activities: DataItem[];
    currentDate: Date;
    stepMode: number;
}

const RentalsTable: React.FC<RentalsTableProps> = ({ activities, currentDate, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const rows = useMemo(() => {
        const dailyRentals = activities
            .map((activity) =>
                activity.dailyRentals.map((rental) => ({
                    ...rental,
                    activityId: activity._id,
                    siteName: activity.siteId.name,
                    reportUserFirstName: activity.reportId.userId.firstName,
                    reportUserLastName: activity.reportId.userId.lastName,
                    rentalTypeDesignation: rental.rentalTypeId.designation,
                    rentalTypeCodeProC: rental.rentalTypeId.codeProC,
                }))
            )
            .flat();

        return dailyRentals.sort((a, b) => {
            if (a.designation > b.designation) return 1;
            if (a.designation < b.designation) return -1;
            if (a.state < b.state) return 1;
            if (a.state > b.state) return -1;
            return 0;
        });
    }, [activities]);

    type RentalRow = (typeof rows)[number];

    const columnDefs: ColDef<RentalRow>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            colId: 'date',
            initialHide: true,
        },
        {
            headerName: capitalizeFirstLetter(t('company')),
            field: 'rentalTypeDesignation',
            colId: 'company',
            initialFlex: 1,
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'rentalTypeCodeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            colId: 'designation',
        },
        {
            headerName: capitalizeFirstLetter(t('state')),
            colId: 'state',
            valueGetter: (params) => capitalizeFirstLetter(t(getMaterialStateInfos(params.data?.state))),
            cellRenderer: (params: ICellRendererParams<RentalRow>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell
                        stateColor={getMaterialStateColor(params.data?.state)}
                        text={capitalizeFirstLetter(t(getMaterialStateInfos(params.data?.state)))}
                    />
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteName',
            colId: 'site',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('used')),
            field: 'isUsed',
            colId: 'used',
        },
        {
            headerName: capitalizeFirstLetter(t('observations')),
            field: 'observations',
            colId: 'observations',
        },
        {
            headerName: capitalizeFirstLetter(t('reportOf')),
            colId: 'reportOf',
            valueGetter: (params) => formatName(params.data?.reportUserFirstName, params.data?.reportUserLastName),
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<RentalRow>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={rows}
            columnDefs={columnDefs}
            exportCsvFileName={`supervision-rentals-${formatDate(currentDate)}`}
            headerRightComponent={
                <>
                    <ExportCsvIcon
                        initDate={currentDate}
                        onExport={(startDate, endDate, fileName, delimiter) =>
                            exportCsv({
                                exportType: exportTypeEnum.RENTALS,
                                queries: { startDate, endDate, delimiter, fileName },
                            })
                        }
                        exportIsLoading={isExportCsvLoading}
                        subTitle={capitalizeFirstLetter(t('rentals'))}
                    />
                </>
            }
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default RentalsTable;
